import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { differenceInSeconds } from 'date-fns';
import DashboardAudioNotificationHelper from 'dashboard/helper/AudioAlerts/DashboardAudioNotificationHelper';
import {
  isAConversationRoute,
  isAInboxViewRoute,
} from 'dashboard/helper/routeHelpers';

const MAX_DISCONNECT_SECONDS = 10800;
// While the socket is down (or the last catch-up failed) the panel asks the server for what it
// missed, instead of waiting for the cable. Steady rate while it works, backing off when it fails.
const FALLBACK_POLL_INTERVAL_MS = 30000;
const FALLBACK_POLL_RETRY_MS = 15000;
const FALLBACK_POLL_MAX_RETRY_MS = 60000;

// A request the browser froze or the network swallowed while the tab was hidden never answers, and
// the banner would wait on it for ever. Only the calls of the reconnect and polling path are
// limited; reports, searches and downloads elsewhere keep their own (long) wait.
export const RECONNECT_REQUEST_TIMEOUT_MS = 30000;

const withDeadline = promise =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('reconnect request timed out')),
      RECONNECT_REQUEST_TIMEOUT_MS
    );
    Promise.resolve(promise)
      .then(resolve, reject)
      .finally(() => clearTimeout(timer));
  });

const latestMessageId = conversation =>
  Math.max(
    conversation.last_non_activity_message?.id || 0,
    ...(conversation.messages || []).map(({ id }) => id || 0)
  );

const canFetchNow = () => navigator.onLine && !document.hidden;

class ReconnectService {
  constructor(store, router) {
    this.store = store;
    this.router = router;
    this.disconnectTime = null;
    this.isSocketDown = false;
    this.needsCatchUp = false;
    this.pollFailures = 0;
    this.pollTimer = null;

    this.setupEventListeners();
  }

  disconnect = () => {
    this.stopFallbackPolling();
    this.removeEventListeners();
  };

  setupEventListeners = () => {
    window.addEventListener('online', this.handleOnlineEvent);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.on(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  removeEventListeners = () => {
    window.removeEventListener('online', this.handleOnlineEvent);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.off(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  getSecondsSinceDisconnect = () =>
    this.disconnectTime
      ? Math.max(differenceInSeconds(new Date(), this.disconnectTime), 0)
      : 0;

  // Force reload if the user is disconnected for more than 3 hours
  handleOnlineEvent = () => {
    if (this.getSecondsSinceDisconnect() >= MAX_DISCONNECT_SECONDS) {
      window.location.reload();
      return;
    }
    this.pollNow();
  };

  handleVisibilityChange = () => {
    if (!document.hidden) this.pollNow();
  };

  shouldPoll = () => this.isSocketDown || this.needsCatchUp;

  getNextPollDelay = () =>
    this.pollFailures
      ? Math.min(
          FALLBACK_POLL_RETRY_MS * 2 ** (this.pollFailures - 1),
          FALLBACK_POLL_MAX_RETRY_MS
        )
      : FALLBACK_POLL_INTERVAL_MS;

  stopFallbackPolling = () => {
    clearTimeout(this.pollTimer);
    this.pollTimer = null;
  };

  scheduleFallbackPoll = (delay = this.getNextPollDelay()) => {
    this.stopFallbackPolling();
    if (this.shouldPoll()) {
      this.pollTimer = setTimeout(this.pollForMissedUpdates, delay);
    }
  };

  // Coming back online or to the foreground is the moment the agent looks at the screen, so the
  // wait for the next tick is skipped.
  pollNow = () => {
    if (this.shouldPoll() && canFetchNow()) this.pollForMissedUpdates();
  };

  pollForMissedUpdates = async () => {
    this.stopFallbackPolling();
    if (canFetchNow()) {
      const wasFailing = this.needsCatchUp && !this.isSocketDown;
      const failed = (await this.catchUp()) === false;
      this.pollFailures = failed ? this.pollFailures + 1 : 0;
      // A catch-up that worked while the socket is back is the end of it; one that failed is retried.
      this.needsCatchUp = failed;
      // The banner went to "could not update" on a failed reconnect; a retry that worked clears it.
      if (!failed && wasFailing)
        emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED);
    }
    this.scheduleFallbackPoll();
  };

  // Message ids only grow, so anything above what the store held before the fetch arrived while
  // the live event was not there to deliver it.
  snapshotLatestMessageIds = () =>
    new Map(
      this.store.getters.getAllConversations.map(chat => [
        chat.id,
        latestMessageId(chat),
      ])
    );

  collectRecoveredMessages = knownIds =>
    this.store.getters.getAllConversations.flatMap(chat => {
      const known = knownIds.get(chat.id) || 0;
      return [chat.last_non_activity_message, ...(chat.messages || [])]
        .filter(message => message && message.id > known)
        .map(message => ({
          ...message,
          conversation_id: chat.id,
          conversation: { assignee_id: chat.meta?.assignee?.id },
        }));
    });

  catchUp = async () => {
    const knownIds = this.snapshotLatestMessageIds();
    let result = false;
    try {
      result = await withDeadline(this.handleRouteSpecificFetch());
    } catch (error) {
      // A request that timed out or was aborted while the tab was hidden: the caller retries.
    }
    DashboardAudioNotificationHelper.onRecoveredMessages(
      this.collectRecoveredMessages(knownIds)
    );
    return result;
  };

  fetchConversations = async () => {
    await this.store.dispatch('updateChatListFilters', {
      page: 1,
      updatedWithin: null,
    });
    // Page 1 REPLACES the list rather than merging into it. The merge only ever adds or replaces,
    // so a conversation that left this tab while the socket was down would otherwise keep its
    // stale copy on screen; replacing is what takes it off.
    return this.store.dispatch('fetchAllConversations', {
      replaceExisting: true,
      timeout: RECONNECT_REQUEST_TIMEOUT_MS,
    });
  };

  // The store action applies the agent's sort itself, so page 1 here is page 1 of what they are
  // looking at, and `replaceExisting` swaps the list for it instead of merging a stale one.
  fetchFilteredOrSavedConversations = async queryData => {
    try {
      return await this.store.dispatch('fetchFilteredConversations', {
        queryData,
        page: 1,
        replaceExisting: true,
        timeout: RECONNECT_REQUEST_TIMEOUT_MS,
      });
    } catch (error) {
      // Reconnect flow should continue; the caller retries the catch-up
      return false;
    }
  };

  fetchConversationsOnReconnect = async () => {
    const {
      getAppliedConversationFiltersQuery,
      'customViews/getActiveConversationFolder': activeFolder,
    } = this.store.getters;
    const query = getAppliedConversationFiltersQuery?.payload?.length
      ? getAppliedConversationFiltersQuery
      : activeFolder?.query;
    if (query) {
      return this.fetchFilteredOrSavedConversations(query);
    }
    return this.fetchConversations();
  };

  refreshActiveConversationOnReconnect = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (!conversationId) return true;
    await this.store.dispatch('getConversation', Number(conversationId));
    return this.store.dispatch('syncActiveConversationMessages', {
      conversationId: Number(conversationId),
    });
  };

  fetchNotificationsOnReconnect = async filter => {
    await this.store.dispatch('notifications/index', { ...filter, page: 1 });
  };

  revalidateCaches = async () => {
    const { label, inbox, team } = await this.store.dispatch(
      'accounts/getCacheKeys'
    );
    await Promise.all([
      this.store.dispatch('labels/revalidate', { newKey: label }),
      this.store.dispatch('inboxes/revalidate', { newKey: inbox }),
      this.store.dispatch('teams/revalidate', { newKey: team }),
    ]);
  };

  // Resolves false when the server could not be reached, so the catch-up is tried again.
  handleRouteSpecificFetch = async () => {
    const currentRoute = this.router.currentRoute.value.name;
    if (isAConversationRoute(currentRoute, true)) {
      const listOk = await this.fetchConversationsOnReconnect();
      const activeOk = await this.refreshActiveConversationOnReconnect();
      return listOk !== false && activeOk !== false;
    }
    if (isAInboxViewRoute(currentRoute, true)) {
      await this.fetchNotificationsOnReconnect(
        this.store.getters['notifications/getNotificationFilters']
      );
    }
    return true;
  };

  setConversationLastMessageId = async () => {
    const { conversation_id: conversationId } =
      this.router.currentRoute.value.params;
    if (conversationId) {
      await this.store.dispatch('setConversationLastMessageId', {
        conversationId: Number(conversationId),
      });
    }
  };

  onDisconnect = () => {
    this.disconnectTime = new Date();
    this.isSocketDown = true;
    this.setConversationLastMessageId();
    this.scheduleFallbackPoll(FALLBACK_POLL_RETRY_MS);
  };

  onReconnect = async () => {
    this.isSocketDown = false;
    this.stopFallbackPolling();
    this.needsCatchUp = (await this.catchUp()) === false;
    this.pollFailures = this.needsCatchUp ? 1 : 0;
    this.scheduleFallbackPoll();
    try {
      await withDeadline(
        (async () => {
          await this.revalidateCaches();
          // Pin events that fired while the socket was down are lost, so the map is rebuilt from the server.
          await this.store.dispatch('conversationPins/fetch');
        })()
      );
    } catch (error) {
      // The banner must not wait on a cache refresh that failed; the next reconnect redoes it.
    }
    emitter.emit(
      this.needsCatchUp
        ? BUS_EVENTS.WEBSOCKET_RECONNECT_FAILED
        : BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED
    );
  };
}

export default ReconnectService;
